import axios from 'axios';
import Cookies from 'js-cookie';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const api = axios.create({
  baseURL: `${BACKEND_URL}/api`,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use((config) => {
  const token = Cookies.get('session_token') || localStorage.getItem('session_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Clear auth state on 401
      Cookies.remove('session_token');
      localStorage.removeItem('session_token');
      localStorage.removeItem('user');
      // Only redirect if not already on auth pages
      if (!window.location.pathname.includes('/login') && !window.location.pathname.includes('/register')) {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// Auth APIs
export const authAPI = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
  logout: () => api.post('/auth/logout'),
  getMe: () => api.get('/auth/me'),
  processSession: (sessionId) => api.post(`/auth/session?session_id=${sessionId}`),
  forgotPassword: (email) => api.post('/auth/forgot-password', { email }),
  resetPassword: (data) => api.post('/auth/reset-password', data),
};

// User APIs
export const userAPI = {
  getProfile: () => api.get('/users/profile'),
  getProfileStatus: () => api.get('/users/profile-status'),
  updateProfile: (data) => api.put('/users/profile', data),
  completeProfile: (data) => api.post('/users/complete-profile', data),
  uploadFiscalConstancy: (formData) => api.post('/users/fiscal-constancy', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  updateRole: (role) => api.put('/users/role', { role }),
  updateSpecialties: (specialties) => api.put('/users/specialties', { specialties }),
  getUserById: (userId) => api.get(`/users/${userId}`),
  getTransactions: () => api.get('/transactions'),
};

// Project APIs
export const projectAPI = {
  create: (data) => api.post('/projects', data),
  getAll: (status) => api.get('/projects', { params: { status } }),
  getById: (projectId) => api.get(`/projects/${projectId}`),
  update: (projectId, data) => api.put(`/projects/${projectId}`, data),
  delete: (projectId) => api.delete(`/projects/${projectId}`),
  send: (projectId) => api.post(`/projects/${projectId}/send`),
  getSummary: (projectId) => api.get(`/projects/${projectId}/summary`),
  uploadExcel: (formData) => api.post('/projects/upload-excel', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
};

// Quotation APIs
export const quotationAPI = {
  getAll: (params) => api.get('/quotations', { params }),
  getById: (quotationId) => api.get(`/quotations/${quotationId}`),
  getProposals: (quotationId) => api.get(`/quotations/${quotationId}/proposals`),
  selectWinner: (quotationId, proposalId) => api.post(`/quotations/${quotationId}/select-winner`, { proposal_id: proposalId }),
  payCommission: (quotationId) => api.post(`/quotations/${quotationId}/pay-commission`),
  getPaymentStatus: (quotationId) => api.get(`/quotations/${quotationId}/payment-status`),
  downloadTemplate: () => api.get('/template/materials', { responseType: 'blob' }),
  downloadContractPDF: (quotationId) => api.get(`/quotations/${quotationId}/contract-pdf`, { responseType: 'blob' }),
};

// Proposal APIs
export const proposalAPI = {
  getAvailable: () => api.get('/proposals/available'),
  getMine: (status) => api.get('/proposals/mine', { params: { status } }),
  create: (data) => api.post('/proposals', data),
  getById: (proposalId) => api.get(`/proposals/${proposalId}`),
  acceptSale: (proposalId) => api.post(`/proposals/${proposalId}/accept-sale`),
  rejectSale: (proposalId, reason) => api.post(`/proposals/${proposalId}/reject-sale`, { reason }),
  payCommission: (proposalId) => api.post(`/proposals/${proposalId}/pay-commission`),
  getDrafts: () => api.get('/proposals/drafts'),
  getDraft: (quotationId) => api.get(`/proposals/drafts/${quotationId}`),
  saveDraft: (data) => api.post('/proposals/drafts', data),
  deleteDraft: (quotationId) => api.delete(`/proposals/drafts/${quotationId}`),
};

// Chat APIs
export const chatAPI = {
  getRoom: (quotationId) => api.get(`/chat/${quotationId}/room`),
  getContactInfo: (quotationId) => api.get(`/chat/${quotationId}/contact-info`),
  getMessages: (quotationId, limit = 50) => api.get(`/chat/${quotationId}/messages`, { params: { limit } }),
  sendMessage: (quotationId, content) => api.post(`/chat/${quotationId}/messages`, { content }),
  getUnread: (quotationId) => api.get(`/chat/${quotationId}/unread`),
  getMyRooms: () => api.get('/chat/my-rooms'),
};

// Category APIs
export const categoryAPI = {
  getAll: () => api.get('/categories'),
  create: (data) => api.post('/categories', data),
};

// Notification APIs
export const notificationAPI = {
  getAll: (unreadOnly = false) => api.get('/notifications', { params: { unread_only: unreadOnly } }),
  markRead: (notificationId) => api.put(`/notifications/${notificationId}/read`),
  markAllRead: () => api.put('/notifications/read-all'),
};

// Rating APIs
export const ratingAPI = {
  getDashboardStats: (activeRole) => api.get('/stats/dashboard', { params: { active_role: activeRole } }),
  canRate: (quotationId) => api.get(`/ratings/can-rate/${quotationId}`),
  create: (data) => api.post('/ratings', data),
  getUserRatings: (userId) => api.get(`/ratings/user/${userId}`),
};

// Subscription APIs
export const subscriptionAPI = {
  getStatus: () => api.get('/subscriptions/status'),
  subscribe: (type) => api.post('/subscriptions/subscribe', { type }),
  getMine: () => api.get('/subscriptions/mine'),
  cancel: (subscriptionId) => api.post(`/subscriptions/${subscriptionId}/cancel`),
};

// Price Consultation APIs
export const priceConsultationAPI = {
  getSubscriptionStatus: () => api.get('/price-consultation/subscription-status'),
  search: (params) => api.get('/price-consultation/search', { params }),
  getPriceData: (params) => api.get('/price-consultation/price-data', { params }),
  getBrands: (params) => api.get('/price-consultation/brands', { params }),
  getCategories: () => api.get('/price-consultation/categories'),
  getStates: (params) => api.get('/price-consultation/states', { params }),
  downloadPDF: (params) => api.get('/price-consultation/price-data-pdf', { params, responseType: 'blob' }),
  getAlerts: () => api.get('/price-consultation/alerts'),
  createAlert: (data) => api.post('/price-consultation/alerts', data),
  deleteAlert: (alertId) => api.delete(`/price-consultation/alerts/${alertId}`),
};

// Admin APIs
export const adminAPI = {
  getStats: () => api.get('/admin/stats'),
  getUsers: (params) => api.get('/admin/users', { params }),
  getTransactions: () => api.get('/admin/transactions'),
  getSubscribers: (statusFilter) => api.get('/admin/subscribers', { params: statusFilter ? { status_filter: statusFilter } : {} }),
  refreshPriceDates: (params) => api.post('/admin/refresh-price-dates', null, { params }),
};

export const fileAPI = {
  uploadFiscalDocument: (formData) => api.post('/files/fiscal-document', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  getFiscalDocumentInfo: () => api.get('/files/fiscal-document'),
  downloadFiscalDocument: () => api.get('/files/fiscal-document/download', {
    responseType: 'blob',
  }),
  deleteFiscalDocument: () => api.delete('/files/fiscal-document'),
  uploadMaterialImage: (formData) => api.post('/files/material-image', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
};

export const pdfAPI = {
  downloadProposalPDF: (proposalId) => api.get(`/proposals/${proposalId}/pdf`, {
    responseType: 'blob',
  }),
  downloadQuotationPDF: (quotationId) => api.get(`/quotations/${quotationId}/pdf`, {
    responseType: 'blob',
  }),
};

export const emailAPI = {
  getStatus: () => api.get('/email/status'),
  getPreferences: () => api.get('/email/preferences'),
  updatePreferences: (prefs) => api.put('/email/preferences', prefs),
  getHistory: () => api.get('/email/history'),
  sendTest: (data) => api.post('/email/test', data),
};

export const analyticsAPI = {
  getSummary: () => api.get('/analytics/summary'),
  getTimeline: () => api.get('/analytics/timeline'),
  getCategories: () => api.get('/analytics/categories'),
  getConversion: () => api.get('/analytics/conversion'),
};

export const templateAPI = {
  list: () => api.get('/templates'),
  create: (data) => api.post('/templates', data),
  delete: (id) => api.delete(`/templates/${id}`),
};

export default api;
